import { FC, FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import "./styles.scss";
import { PageShell } from "../PageShell";
import { useTranslation } from "../../../hooks/useTranslation";
import { publicAPI } from "../../../backend/api/api";

const CONTACT_PHONE = "575-741-0444";
const CLEAN_PHONE = CONTACT_PHONE.replace(/-/g, "");

export const ContactUsPage: FC = () => {
  const { p } = useTranslation("landing_contact");
  const { p: pc, t } = useTranslation("contact_us");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = (): boolean => {
    let valid = true;

    if (!name.trim()) {
      setNameError(pc("name_required"));
      valid = false;
    } else {
      setNameError(null);
    }

    if (!email.trim()) {
      setEmailError(pc("email_required"));
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(pc("invalid_email"));
      valid = false;
    } else {
      setEmailError(null);
    }

    return valid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError(null);
    try {
      await publicAPI.contactUs({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(pc("error_submit"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="landing-contact-page">
        <h1 className="landing-contact-page__heading">{p("heading")}</h1>
        <p className="landing-contact-page__subtitle">{p("subtitle")}</p>

        <div className="landing-contact-page__grid">
          <div className="landing-contact-page__quick">
            <h2 className="landing-contact-page__quick-heading">{p("quick_heading")}</h2>
            <a
              className="landing-contact-page__quick-link landing-contact-page__quick-link--whatsapp"
              href={`https://wa.me/1${CLEAN_PHONE}`}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
            <a
              className="landing-contact-page__quick-link landing-contact-page__quick-link--sms"
              href={`sms:+1${CLEAN_PHONE}`}
            >
              {pc("text_message")}
            </a>
            <p className="landing-contact-page__phone">{CONTACT_PHONE}</p>
          </div>

          <div className="landing-contact-page__card">
            {submitted ? (
              <div className="landing-contact-page__success">
                <div className="landing-contact-page__success-icon" aria-hidden="true">✓</div>
                <h2 className="landing-contact-page__success-heading">{pc("message_sent")}</h2>
                <p className="landing-contact-page__success-text">{pc("thank_you", { email })}</p>
              </div>
            ) : (
              <form className="landing-contact-page__form" onSubmit={handleSubmit} noValidate>
                <p className="landing-contact-page__form-intro">{pc("intro")}</p>

                {submitError && (
                  <p className="landing-contact-page__form-error" role="alert">{submitError}</p>
                )}

                <div className="landing-contact-page__field">
                  <label className="landing-contact-page__label" htmlFor="contact-name">
                    {t("general.name")}
                  </label>
                  <input
                    id="contact-name"
                    className={
                      "landing-contact-page__input" +
                      (nameError ? " landing-contact-page__input--error" : "")
                    }
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                  />
                  {nameError && <span className="landing-contact-page__field-error">{nameError}</span>}
                </div>

                <div className="landing-contact-page__field">
                  <label className="landing-contact-page__label" htmlFor="contact-email">
                    {t("general.email")}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    className={
                      "landing-contact-page__input" +
                      (emailError ? " landing-contact-page__input--error" : "")
                    }
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError(null);
                    }}
                  />
                  {emailError && <span className="landing-contact-page__field-error">{emailError}</span>}
                </div>

                <div className="landing-contact-page__field">
                  <label className="landing-contact-page__label" htmlFor="contact-phone">
                    {pc("cell_phone")}
                    <span className="landing-contact-page__label-optional">{pc("optional")}</span>
                  </label>
                  <input
                    id="contact-phone"
                    type="tel"
                    className="landing-contact-page__input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>

                <div className="landing-contact-page__field">
                  <label className="landing-contact-page__label" htmlFor="contact-message">
                    {pc("comment_message")}
                  </label>
                  <textarea
                    id="contact-message"
                    className="landing-contact-page__input landing-contact-page__input--textarea"
                    rows={5}
                    placeholder={pc("help_placeholder")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                <button type="submit" className="landing-contact-page__submit" disabled={loading}>
                  {loading ? pc("sending") : pc("send_message")}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* The privacy policy is deliberately kept out of the 6-tab nav, so
            this is its one in-app entry point besides the direct URL. */}
        <p className="landing-contact-page__privacy">
          <Link className="landing-contact-page__privacy-link" to="/privacy-policy">
            {p("privacy_link")}
          </Link>
        </p>
      </div>
    </PageShell>
  );
};
